import * as THREE from 'three';

/**
 * Surface-mounted trim (cage ribs glued on glass) must not appear in Three.js
 * screen-space transmission. Exclude them from the refraction buffer only.
 *
 * `object.visible` / layers are too late (render list already built). Toggle
 * `material.colorWrite` / `depthWrite` in onBeforeRender — renderBufferDirect
 * reads those for the draw.
 */

type WriteState = {
  mat: THREE.Material;
  colorWrite: boolean;
  depthWrite: boolean;
};

const _txRtSet = new WeakSet<object>();
const _drawSize = new THREE.Vector2();

function looksLikeTransmissionRT(
  renderer: THREE.WebGLRenderer,
  rt: THREE.WebGLRenderTarget,
): boolean {
  if ((rt as { isWebGLCubeRenderTarget?: boolean }).isWebGLCubeRenderTarget) return false;
  const tex = rt.texture as THREE.Texture | THREE.Texture[] | undefined;
  if (!tex || Array.isArray(tex)) return false;
  if (!tex.generateMipmaps) return false;
  if (tex.minFilter !== THREE.LinearMipmapLinearFilter) return false;

  // Three.js transmission RT defaults to samples: 4.
  if ((rt.samples ?? 0) >= 1) return true;

  // Some platforms drop MSAA — match size vs transmissionResolutionScale.
  const scale = (renderer as THREE.WebGLRenderer & { transmissionResolutionScale?: number })
    .transmissionResolutionScale ?? 1;
  renderer.getDrawingBufferSize(_drawSize);
  const tw = Math.round(_drawSize.x * scale);
  const th = Math.round(_drawSize.y * scale);
  return Math.abs(rt.width - tw) <= 2 && Math.abs(rt.height - th) <= 2;
}

/** Patch once: flag while Three.js transmission RT is bound. */
export function installTransmissionPassTracker(renderer: THREE.WebGLRenderer): void {
  const gl = renderer as THREE.WebGLRenderer & {
    __c3dTxTrack?: boolean;
    __c3dTransmissionPass?: boolean;
  };
  if (gl.__c3dTxTrack) return;
  gl.__c3dTxTrack = true;

  const orig = renderer.setRenderTarget.bind(renderer);
  renderer.setRenderTarget = ((
    rt: THREE.WebGLRenderTarget | THREE.WebGLRenderTarget[] | null,
    ...rest: unknown[]
  ) => {
    let inTx = false;
    if (rt && !Array.isArray(rt)) {
      if (_txRtSet.has(rt) || looksLikeTransmissionRT(renderer, rt)) {
        // Drop MSAA — 4× at supersampled DPR overflows GPU allocation and
        // leaves incomplete zero-size FBOs that break the whole frame.
        if ((rt.samples ?? 0) > 0) rt.samples = 0;
        _txRtSet.add(rt);
        inTx = true;
      }
    }
    gl.__c3dTransmissionPass = inTx;
    return (orig as Function)(rt, ...rest);
  }) as typeof renderer.setRenderTarget;
}

export function isTransmissionRenderPass(renderer: THREE.WebGLRenderer): boolean {
  const gl = renderer as THREE.WebGLRenderer & { __c3dTransmissionPass?: boolean };
  if (gl.__c3dTransmissionPass) return true;
  const rt = renderer.getRenderTarget();
  return !!(rt && !Array.isArray(rt) && _txRtSet.has(rt));
}

function isTransmitterMesh(obj: THREE.Object3D): boolean {
  if (!(obj instanceof THREE.Mesh)) return false;
  const mat = obj.material;
  if (Array.isArray(mat)) return false;
  return mat instanceof THREE.MeshPhysicalMaterial && (mat.transmission ?? 0) > 0.001;
}

function materialsOf(mesh: THREE.Mesh): THREE.Material[] {
  return Array.isArray(mesh.material)
    ? mesh.material.filter(Boolean)
    : mesh.material
      ? [mesh.material]
      : [];
}

function beginExcludeDraw(mesh: THREE.Mesh): void {
  const states: WriteState[] = [];
  for (const mat of materialsOf(mesh)) {
    states.push({
      mat,
      colorWrite: mat.colorWrite !== false,
      depthWrite: mat.depthWrite !== false,
    });
    mat.colorWrite = false;
    mat.depthWrite = false;
  }
  mesh.userData.c3dTxWriteState = states;
}

function endExcludeDraw(mesh: THREE.Mesh): void {
  const states = mesh.userData.c3dTxWriteState as WriteState[] | undefined;
  if (!states) return;
  for (const s of states) {
    s.mat.colorWrite = s.colorWrite;
    s.mat.depthWrite = s.depthWrite;
  }
  delete mesh.userData.c3dTxWriteState;
}

/**
 * True when this object (or an ancestor) is marked excludeFromRefraction,
 * or matches glued cage naming. Explicit `false` wins.
 */
export function shouldExcludeFromRefraction(obj: THREE.Object3D): boolean {
  let o: THREE.Object3D | null = obj;
  while (o) {
    if (o.userData.excludeFromRefraction === false) return false;
    if (o.userData.excludeFromRefraction === true) return true;
    if (/decanter\s*metal|metal\s*-\s*element|metal\s*-\s*body|metal\s*-\s*cap/i.test(o.name)) {
      return true;
    }
    o = o.parent;
  }
  return false;
}

export function transmissionFrontCullInstancedBeforeRender(
  this: THREE.InstancedMesh,
  renderer: THREE.WebGLRenderer,
): void {
  if (!isTransmissionRenderPass(renderer)) return;
  if (!shouldExcludeFromRefraction(this)) return;
  beginExcludeDraw(this);
}

export function transmissionFrontCullInstancedAfterRender(this: THREE.InstancedMesh): void {
  endExcludeDraw(this);
}

export function transmissionFrontCullMeshBeforeRender(
  this: THREE.Mesh,
  renderer: THREE.WebGLRenderer,
): void {
  if (!isTransmissionRenderPass(renderer)) return;
  if (isTransmitterMesh(this)) return;
  if (!shouldExcludeFromRefraction(this)) return;
  beginExcludeDraw(this);
}

export function transmissionFrontCullMeshAfterRender(this: THREE.Mesh): void {
  endExcludeDraw(this);
}

export type TxCullState = {
  centers: THREE.Vector3[];
  bias: number;
  maxSize: number;
  boxes: THREE.Box3[];
};

/** No-op state bump so NestedTransmissionApplier keeps a stable call site. */
export function updateTransmissionCullState(root: THREE.Object3D): TxCullState {
  const empty: TxCullState = { centers: [], bias: 0, maxSize: 0, boxes: [] };
  root.userData.c3dTxCull = empty;
  return empty;
}

/** Clear any stuck write-state from interrupted frames / HMR. */
export function restoreStuckTransmissionWriteState(root: THREE.Object3D): void {
  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    if (!obj.userData.c3dTxWriteState && !obj.userData.c3dTxMatWrite) return;
    endExcludeDraw(obj);
    if (obj.userData.c3dTxMatWrite) {
      const legacy = obj.userData.c3dTxMatWrite as WriteState[];
      for (const s of legacy) {
        s.mat.colorWrite = s.colorWrite;
        s.mat.depthWrite = s.depthWrite;
      }
      delete obj.userData.c3dTxMatWrite;
    }
  });
}

export function ensureTransmissionFrontCullHooks(root: THREE.Object3D): void {
  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    if (obj instanceof THREE.InstancedMesh) {
      // Handlers installed by ModifiedMesh; ensure flag propagates from source naming.
      if (shouldExcludeFromRefraction(obj)) {
        obj.userData.excludeFromRefraction = true;
      }
      return;
    }
    if (isTransmitterMesh(obj)) return;
    if (!shouldExcludeFromRefraction(obj)) return;
    if (obj.userData.c3dTxFrontCullHooked) return;
    if ((obj.renderOrder ?? 0) >= 20000) return;

    obj.userData.c3dTxFrontCullHooked = true;
    obj.userData.excludeFromRefraction = true;
    const prevBefore = obj.onBeforeRender.bind(obj);
    const prevAfter = obj.onAfterRender.bind(obj);
    obj.onBeforeRender = (renderer, scene, camera, geometry, material, group) => {
      prevBefore(renderer, scene, camera, geometry, material, group);
      transmissionFrontCullMeshBeforeRender.call(obj, renderer);
    };
    obj.onAfterRender = (renderer, scene, camera, geometry, material, group) => {
      transmissionFrontCullMeshAfterRender.call(obj);
      prevAfter(renderer, scene, camera, geometry, material, group);
    };
  });
}
